/**
 * Background sync for offline POS data (desktop app only).
 * Fetches products, customers, salesmen from API and stores in local SQLite via electronAPI.
 * Syncs pending offline bills to backend when online (Phase 4 reconciliation).
 * Only runs when window.electronAPI exists - web and Android are unaffected.
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { isAuthenticated, getCurrentUser } from '../../services/authService';
import productService from '../../services/productService';
import { customerService } from '../../services/customerService';
import salesmanService from '../../services/salesmanService';
import salesService from '../../services/salesService';
import { OfflineContext } from '../../context/OfflineContext';

const SYNC_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const INITIAL_SYNC_DELAY_MS = 5000; // 5 seconds after mount (let app settle)
const RECONCILIATION_INTERVAL_MS = 30 * 1000; // 30 seconds - push pending bills when online
const RECONCILIATION_INITIAL_DELAY_MS = 1000; // 1 second - run sync soon after coming online
const PENDING_POLL_INTERVAL_MS = 5000; // 5 seconds - refresh pending count for indicator

/**
 * Normalize location product to common product format for offline DB.
 */
function normalizeLocationProduct(lp) {
  return {
    id: lp.productId,
    name: lp.productName,
    sku: lp.sku || lp.productCode,
    barcode: lp.barcode || lp.productBarcode || lp.productCode,
    sellingPrice: lp.sellingPrice ?? lp.purchasePrice ?? 0,
    wholesalePrice: lp.wholesalePrice ?? lp.wholesale_price ?? null,
    quantity: lp.availableForSale ?? lp.quantity ?? 0,
    unit: lp.unit || lp.productUnit || null,
    tax: lp.tax || null,
    taxId: lp.taxId ?? lp.tax?.id ?? null,
    taxRate: lp.tax?.rate ?? lp.taxRate ?? 0,
    companyId: lp.companyId ?? lp.company_id ?? null,
    locationId: lp.locationId ?? lp.location_id ?? null,
    status: lp.status || 'ACTIVE',
    parentProductId: lp.parentProductId ?? lp.parent_product_id ?? null
  };
}

/**
 * Normalize warehouse product (already in correct format, ensure fields exist).
 */
function normalizeProduct(p) {
  return {
    id: p.id,
    name: p.name,
    sku: p.sku,
    barcode: p.barcode,
    sellingPrice: p.sellingPrice ?? p.selling_price ?? 0,
    wholesalePrice: p.wholesalePrice ?? p.wholesale_price ?? null,
    quantity: p.quantity ?? 0,
    unit: p.unit || null,
    tax: p.tax || null,
    taxId: p.taxId ?? p.tax?.id ?? null,
    taxRate: p.tax?.rate ?? p.taxRate ?? 0,
    companyId: p.companyId ?? p.company_id ?? null,
    locationId: p.locationId ?? p.location_id ?? null,
    status: p.status || 'ACTIVE',
    parentProductId: p.parentProductId ?? p.parent_product_id ?? null
  };
}

/**
 * Get walk-in customer ID from offline customers (for bills with no customer selected).
 */
async function getWalkInCustomerId(electronAPI) {
  if (!electronAPI?.getOfflineCustomers) return null;
  const customers = await electronAPI.getOfflineCustomers() || [];
  const walkIn = customers.find(
    (c) => c && (String(c.name || '').toLowerCase().includes('walk-in') || String(c.name || '').toLowerCase().includes('walkin'))
  );
  return walkIn?.id ?? null;
}

/**
 * Run reconciliation: push pending offline bills to backend when online.
 * Called periodically and on manual "Sync now" button.
 * @returns {Promise<number>} Number of bills successfully synced
 */
async function runReconciliation(electronAPI, onMarkSynced, onMarkFailed) {
  if (!electronAPI?.getOfflinePendingOrders || !electronAPI?.markOrderSynced || !electronAPI?.markOrderSyncFailed) return 0;
  const pending = await electronAPI.getOfflinePendingOrders();
  if (!pending || pending.length === 0) return 0;
  let walkInId = null;
  let syncedCount = 0;
  for (const row of pending) {
    const { local_id, order_data } = row;
    if (!order_data || !order_data.items?.length) {
      await onMarkFailed(local_id, 'Invalid order data');
      continue;
    }
    const payload = { ...order_data };
    if (payload.customerId == null) {
      walkInId = walkInId ?? await getWalkInCustomerId(electronAPI);
      if (walkInId != null) payload.customerId = walkInId;
      else {
        await onMarkFailed(local_id, 'Customer required. Add Walk-in Customer and sync data before going offline.');
        continue;
      }
    }
    try {
      const result = await salesService.createSalesOrder(payload);
      const backendId = result?.id ?? result?.orderId ?? null;
      if (backendId != null) {
        await onMarkSynced(local_id, backendId);
        syncedCount++;
      } else {
        await onMarkFailed(local_id, 'Backend did not return order ID');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      await onMarkFailed(local_id, msg);
    }
  }
  return syncedCount;
}

/**
 * OfflineSyncProvider - runs background sync when in desktop app and user is logged in.
 * Also syncs pending offline bills to backend when online (Phase 4 reconciliation).
 * Provides OfflineContext for pending count and manual sync.
 */
export default function OfflineSyncProvider({ children }) {
  const intervalRef = useRef(null);
  const reconciliationRef = useRef(null);
  const pendingPollRef = useRef(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const refreshPendingCount = useCallback(async () => {
    if (!window.electronAPI?.getOfflinePendingCount) return;
    try {
      const count = await window.electronAPI.getOfflinePendingCount();
      const n = count ?? 0;
      setPendingCount(n);
      if (n > 0) console.log('[OfflineSync] Pending bills:', n);
    } catch (e) {
      console.warn('[OfflineSync] getOfflinePendingCount failed:', e);
      setPendingCount(0);
    }
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const runReconciliationWithContext = useCallback(async (showSnackbar = false) => {
    if (!window.electronAPI || !isAuthenticated()) return;
    setIsSyncing(true);
    let lastError = null;
    try {
      const pending = await window.electronAPI.getOfflinePendingOrders();
      const beforeCount = pending?.length ?? 0;
      const syncedCount = await runReconciliation(
        window.electronAPI,
        (localId, backendId) => window.electronAPI.markOrderSynced(localId, backendId),
        (localId, err) => { lastError = err; return window.electronAPI.markOrderSyncFailed(localId, err); }
      );
      await refreshPendingCount();
      // Always show notification when bills sync (manual or automatic)
      if (syncedCount > 0) {
        enqueueSnackbar(`${syncedCount} offline bill${syncedCount > 1 ? 's' : ''} synced successfully`, { variant: 'success' });
      }
      if (showSnackbar && beforeCount > 0 && syncedCount === 0 && lastError) {
        enqueueSnackbar(`Sync failed: ${lastError}`, { variant: 'error' });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Sync failed';
      if (showSnackbar) enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setIsSyncing(false);
    }
  }, [refreshPendingCount, enqueueSnackbar]);

  const runSync = useCallback(async () => {
    if (!window.electronAPI || !isAuthenticated()) return;

    try {
      const user = getCurrentUser();
      const hasLocation = user?.location?.id != null;

      // 1. Fetch products (location-specific or all)
      let products = [];
      try {
        if (hasLocation) {
          const locationProducts = await productService.getLocationProducts();
          products = (locationProducts || []).map(normalizeLocationProduct);
        } else {
          const res = await productService.getAllProducts(0, 1000, 'name', 'asc');
          const list = Array.isArray(res) ? res : (res?.content || []);
          products = list.map(normalizeProduct);
        }
        if (products.length > 0 && window.electronAPI.upsertOfflineProducts) {
          await window.electronAPI.upsertOfflineProducts(products);
          console.log('[OfflineSync] Products synced:', products.length);
        }
      } catch (e) {
        console.warn('[OfflineSync] Products fetch failed:', e?.message || e);
      }

      // 2. Fetch customers
      try {
        const res = await customerService.getAllCustomers({ page: 0, size: 500, sort: 'name', direction: 'ASC' });
        const customers = Array.isArray(res) ? res : (res?.content || []);
        if (customers.length > 0 && window.electronAPI.upsertOfflineCustomers) {
          await window.electronAPI.upsertOfflineCustomers(customers);
          console.log('[OfflineSync] Customers synced:', customers.length);
        }
      } catch (e) {
        console.warn('[OfflineSync] Customers fetch failed:', e?.message || e);
      }

      // 3. Fetch salesmen
      try {
        const locationId = user?.location?.id ?? null;
        const list = await salesmanService.list(locationId != null ? { locationId } : {});
        const salesmen = Array.isArray(list) ? list : [];
        if (salesmen.length > 0 && window.electronAPI.upsertOfflineSalesmen) {
          await window.electronAPI.upsertOfflineSalesmen(salesmen);
          console.log('[OfflineSync] Salesmen synced:', salesmen.length);
        }
      } catch (e) {
        console.warn('[OfflineSync] Salesmen fetch failed:', e?.message || e);
      }
    } catch (err) {
      console.warn('[OfflineSync] Sync error:', err?.message || err);
    }
  }, []);

  useEffect(() => {
    if (!window.electronAPI) {
      console.log('[OfflineSync] Skipped: not in Electron');
      return;
    }
    if (!isAuthenticated()) {
      console.log('[OfflineSync] Skipped: not authenticated');
      return;
    }
    console.log('[OfflineSync] Started: polling pending count, reconciliation every 30s');

    // Initial sync after delay
    const initialTimer = setTimeout(runSync, INITIAL_SYNC_DELAY_MS);

    // Periodic sync (products, customers, salesmen)
    intervalRef.current = setInterval(runSync, SYNC_INTERVAL_MS);

    // Phase 4: Reconciliation - run soon after mount, then every 30s
    const initialReconcile = setTimeout(() => runReconciliationWithContext(true), RECONCILIATION_INITIAL_DELAY_MS);
    reconciliationRef.current = setInterval(runReconciliationWithContext, RECONCILIATION_INTERVAL_MS);

    // Poll pending count for indicator (every 5s)
    refreshPendingCount();
    pendingPollRef.current = setInterval(refreshPendingCount, PENDING_POLL_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(initialReconcile);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (reconciliationRef.current) {
        clearInterval(reconciliationRef.current);
        reconciliationRef.current = null;
      }
      if (pendingPollRef.current) {
        clearInterval(pendingPollRef.current);
        pendingPollRef.current = null;
      }
    };
  }, [runSync, runReconciliationWithContext, refreshPendingCount]);

  const fetchPendingOrders = useCallback(async () => {
    if (!window.electronAPI?.getOfflinePendingOrders) return [];
    try {
      return await window.electronAPI.getOfflinePendingOrders() || [];
    } catch {
      return [];
    }
  }, []);

  const contextValue = window.electronAPI
    ? { pendingCount, isSyncing, syncNow: () => runReconciliationWithContext(true), refreshPendingCount, fetchPendingOrders }
    : { pendingCount: 0, isSyncing: false, syncNow: () => {}, refreshPendingCount: () => {}, fetchPendingOrders: () => Promise.resolve([]) };

  return (
    <OfflineContext.Provider value={contextValue}>
      {children}
    </OfflineContext.Provider>
  );
}
